// Pure JSON-LD builders for structured data (schema.org).
// Values here describe the organization and MUST match publicly verifiable facts.

import { SITE_NAME, SITE_URL } from '@/constants/seo'

export { SITE_URL }

// Absolute, trailing-slash URL for a site-relative path — the canonical form
// every schema `url`/`item` value must use (trailingSlash: true).
export function absUrl(path: string): string {
  const withLead = path.startsWith('/') ? path : `/${path}`
  if (withLead === '/') return `${SITE_URL}/`
  return `${SITE_URL}${withLead.replace(/\/+$/, '')}/`
}

// Branch / SFC operating hours — mirror of the tables shown on /contact-us, the
// single source of truth for these facts.
// West Malaysia: Mon–Thu full day; Fri split around the 12:30–14:30 prayer break.
const WEST_MALAYSIA_HOURS = [
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
    opens: '09:15',
    closes: '16:15'
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Friday'],
    opens: '09:15',
    closes: '12:30'
  },
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Friday'],
    opens: '14:30',
    closes: '16:15'
  }
]
// East Malaysia (Sabah / Sarawak): open Sun–Thu, closed Fri.
const EAST_MALAYSIA_HOURS = [
  {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
    opens: '09:15',
    closes: '16:15'
  }
]

/**
 * Site-wide Organization schema, typed as BankOrCreditUnion (more specific than
 * Organization). Emitted once, site-wide, from the root layout.
 */
export function organizationSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BankOrCreditUnion',
    '@id': `${SITE_URL}/#organization`,
    name: 'alrajhi bank Malaysia',
    legalName: 'Al Rajhi Banking & Investment Corporation (Malaysia) Berhad',
    alternateName: ['Al Rajhi Bank Malaysia', 'ARBM'],
    url: `${SITE_URL}/`,
    // Self-hosted, crawlabe logo (Google ignores third-party/Wikipedia URLs).
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
      width: 600,
      height: 283
    },
    image: `${SITE_URL}/og-default.png`,
    description:
      'Discover Shariah-compliant Islamic banking in Malaysia with alrajhi bank. Explore online banking, savings, financing, and wealth management solutions.',
    foundingDate: '2006',
    telephone: '+60323326000',
    email: 'customersupport@alrajhibank.com.my',
    brand: {
      '@type': 'Brand',
      name: 'alrajhi bank Malaysia'
    },
    // The Malaysian bank is a subsidiary of the global Al Rajhi Bank group.
    parentOrganization: {
      '@type': 'BankOrCreditUnion',
      name: 'Al Rajhi Bank',
      url: 'https://www.alrajhibank.com.sa',
      sameAs: 'https://en.wikipedia.org/wiki/Al_Rajhi_Bank'
    },
    areaServed: {
      '@type': 'Country',
      name: 'Malaysia'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Level 8, Integra Tower, The Intermark, 348 Jalan Tun Razak',
      addressLocality: 'Kuala Lumpur',
      addressRegion: 'Kuala Lumpur',
      postalCode: '50400',
      addressCountry: 'MY'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 3.160806,
      longitude: 101.718639
    },
    openingHoursSpecification: WEST_MALAYSIA_HOURS,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+60323326000',
      contactType: 'customer service',
      areaServed: 'MY',
      availableLanguage: ['English', 'Malay']
    },
    sameAs: [
      'https://www.linkedin.com/company/arbmy/',
      'https://www.instagram.com/alrajhibankmalaysia/',
      'https://www.facebook.com/alrajhibankmalaysia/',
      'https://www.youtube.com/@alrajhibankMalaysiaOfficial'
    ]
  }
}

/**
 * Site-wide WebSite node — establishes the site as an entity and lets Google
 * associate the domain with the publisher Organization. No SearchAction: the
 * site has no URL-addressable search endpoint, and Google requires one to work.
 */
export function websiteSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    alternateName: ['alrajhi bank Malaysia', 'ARBM'],
    inLanguage: 'en-MY',
    publisher: { '@id': `${SITE_URL}/#organization` }
  }
}

/** Turn the CMS/HTML branch address into a single flat postal string. */
function flattenAddress(html: string): string {
  return html
    .replaceAll(/<br\s*\/?>/gi, ', ')
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/\s*,\s*,\s*/g, ', ')
    .replace(/,\s*$/, '')
    .trim()
}

/** Minimal shape of a `branchLocation` entry consumed by `branchSchemas`. */
export interface BranchInput {
  name: string
  phoneNo: string
  type: string
  address: string
  src: string
}

/**
 * Per-branch BankOrCreditUnion schemas, derived from the `branchLocation`
 * constant that powers /contact-us (single source of truth). Each branch links
 * back to the head-office Organization node via `branchOf`, and carries the
 * region-appropriate operating hours. Emit these on the /contact-us page.
 *
 * The branch list is passed in (not imported) so this module — pulled into
 * client bundles by Breadcrumbs/AccordionSimple — never bundles the branch
 * dataset or its embedded Maps URLs.
 */
export function branchSchemas(branches: BranchInput[]): object[] {
  return branches.map(branch => {
    const isEastMalaysia = /Sabah|Sarawak|Kuching|Kota Kinabalu/i.test(branch.address)
    const isAtm = branch.type === 'ATM'
    const slug = branch.name
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    return {
      '@context': 'https://schema.org',
      '@type': isAtm ? 'AutomatedTeller' : 'BankOrCreditUnion',
      '@id': `${SITE_URL}/contact-us/#${slug}`,
      name: `alrajhi bank Malaysia — ${branch.name}`,
      branchOf: { '@id': `${SITE_URL}/#organization` },
      parentOrganization: { '@id': `${SITE_URL}/#organization` },
      url: `${SITE_URL}/contact-us/`,
      telephone: branch.phoneNo,
      hasMap: branch.src,
      address: {
        '@type': 'PostalAddress',
        streetAddress: flattenAddress(branch.address),
        addressCountry: 'MY'
      },
      // ATMs are self-service and available 24/7; only staffed branches carry hours.
      ...(isAtm
        ? {}
        : { openingHoursSpecification: isEastMalaysia ? EAST_MALAYSIA_HOURS : WEST_MALAYSIA_HOURS })
    }
  })
}

// ---------------------------------------------------------------------------
// FAQPage
// ---------------------------------------------------------------------------

export interface FaqEntry {
  question: string
  answer: string
}

// Collapse CMS HTML to a clean plain-text string. FAQPage answers accept a
// limited HTML subset, but CMS markup carries class/style attributes that make
// the node invalid — plain text is the safest, portable choice.
function stripHtml(input: string): string {
  return input
    .replaceAll(/<[^>]+>/g, ' ')
    .replaceAll(/&nbsp;/gi, ' ')
    .replaceAll(/&amp;/gi, '&')
    .replaceAll(/&lt;/gi, '<')
    .replaceAll(/&gt;/gi, '>')
    .replaceAll(/&#39;|&rsquo;|&lsquo;/gi, "'")
    .replaceAll(/&quot;|&ldquo;|&rdquo;/gi, '"')
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/\s+([?.!,;:])/g, '$1')
    .trim()
}

// FAQPage node built from the exact Q&A pairs rendered on the page (single
// source of truth — Google requires the FAQ content to be visible on-page).
// Returns null when there are no valid entries so callers can skip rendering.
export function faqPageSchema(entries: FaqEntry[]): object | null {
  const mainEntity = entries
    .map(entry => ({
      question: stripHtml(entry.question ?? ''),
      answer: stripHtml(entry.answer ?? '')
    }))
    .filter(entry => entry.question && entry.answer)
    .map(entry => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: { '@type': 'Answer', text: entry.answer }
    }))

  if (mainEntity.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  }
}

// ---------------------------------------------------------------------------
// BreadcrumbList
// ---------------------------------------------------------------------------

export interface BreadcrumbEntry {
  name: string
  url: string
}

// BreadcrumbList node. Needs at least two levels (Home + current) to be
// meaningful; returns null otherwise so callers can skip rendering.
export function breadcrumbSchema(items: BreadcrumbEntry[]): object | null {
  if (items.length < 2) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}

// ---------------------------------------------------------------------------
// FinancialProduct family (per-product, page-level)
// ---------------------------------------------------------------------------

// The schema.org FinancialProduct subtypes we map product pages onto. Picked to
// match each product's real nature: accounts → BankAccount/DepositAccount,
// financing & cards-on-credit → LoanOrCredit, plain cards → PaymentCard,
// investments → InvestmentOrDeposit, everything else → FinancialProduct/Service.
export type FinancialProductType =
  | 'FinancialProduct'
  | 'LoanOrCredit'
  | 'MortgageLoan' // property-secured financing (most specific LoanOrCredit subtype)
  | 'CreditCard'
  | 'PaymentCard'
  | 'BankAccount'
  | 'DepositAccount'
  | 'InvestmentOrDeposit'
  | 'InvestmentFund' // pooled fund investment (most specific InvestmentOrDeposit subtype)
  | 'Service'

export interface FinancialProductInput {
  type: FinancialProductType
  name: string
  description: string
  /** Site-relative path, e.g. '/personal/financing/home-financing-i'. */
  path: string
  /** LoanOrCredit only — e.g. 'Mortgage', 'Personal financing'. */
  loanType?: string
  /** Service only — e.g. 'Priority Banking'. schema.org/Service.serviceType. */
  serviceType?: string
}

// One node per product page. Reuses the site-wide #organization node as
// `provider` (never re-declares it). Deliberately carries NO rate / fee /
// tenure fields: those are YMYL numbers that would have to match the visible
// page exactly, and a FinancialProduct is valid — and safer — without them.
export function financialProductSchema(input: FinancialProductInput): object {
  return {
    '@context': 'https://schema.org',
    '@type': input.type,
    name: input.name,
    description: input.description,
    url: absUrl(input.path),
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'Malaysia' },
    ...(input.loanType ? { loanType: input.loanType } : {}),
    ...(input.serviceType ? { serviceType: input.serviceType } : {})
  }
}

export interface PersonInput {
  name: string
  /** Site-relative path of this person's profile page. */
  path: string
  /** Board role as shown on the page — e.g. 'Chairman', 'Member'. */
  jobTitle?: string
  /** PLAIN TEXT biography. Strip CMS HTML with excerpt() before passing it. */
  description?: string
  /** Absolute portrait URL. */
  image?: string
}

/**
 * One node per board / Shariah-committee member profile page.
 *
 * `worksFor` points at the site-wide #organization node from the root layout
 * rather than re-declaring the bank, which is what makes the person and the
 * organization resolve as one connected graph to a crawler.
 */
export function personSchema(input: PersonInput): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: input.name,
    url: absUrl(input.path),
    worksFor: { '@id': `${SITE_URL}/#organization` },
    ...(input.jobTitle ? { jobTitle: input.jobTitle } : {}),
    ...(input.description ? { description: input.description } : {}),
    ...(input.image ? { image: input.image } : {})
  }
}
